/**
 * Behaviour signals -> Shopify Customer Events (Shopify.analytics.publish).
 * A custom pixel (docs/customer-events-pixel.js) forwards them to GA4; the same
 * names are tagged in Microsoft Clarity so recordings can be filtered.
 * All event names start with "brisato_". No personal data is sent.
 */
(() => {
  if (window.BrisatoBehavior) return;
  window.BrisatoBehavior = true;

  const send = (name, data = {}) => {
    const event = `brisato_${name}`;
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(event, data);
      }
      if (typeof window.clarity === 'function') window.clarity('event', event);
      document.dispatchEvent(new CustomEvent('brisato:track', { detail: { event, data } }));
    } catch (_) {
      /* analytics must never break the shop */
    }
  };
  window.brisatoTrack = send;

  const page = document.body.dataset.pageType || '';
  const path = window.location.pathname;

  /* ---------- Product cards: seen, clicked, photos, quick add ---------- */
  const listName = (el) => {
    const section = el.closest('.shopify-section');
    const heading = section && section.querySelector('h1, h2');
    return heading ? heading.textContent.trim().slice(0, 60) : page;
  };
  const cardInfo = (card) => {
    const link = card.querySelector('.card__title');
    const list = card.closest('ul');
    const position = list ? Array.prototype.indexOf.call(list.children, card.closest('li')) + 1 : 0;
    return {
      product: link ? link.getAttribute('href').split('?')[0].split('/').pop() : '',
      position,
      list: listName(card),
    };
  };

  let seenQueue = [];
  const seen = new WeakSet();
  const flushSeen = () => {
    if (!seenQueue.length) return;
    send('products_seen', { products: seenQueue.map((c) => `${c.product}@${c.position}`).join(','), list: seenQueue[0].list, count: seenQueue.length });
    seenQueue = [];
  };
  const timers = new WeakMap();
  const cardObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        if (entry.isIntersecting) {
          // counts as "seen" after 1 s at least half on screen
          timers.set(card, window.setTimeout(() => {
            if (seen.has(card)) return;
            seen.add(card);
            seenQueue.push(cardInfo(card));
            if (seenQueue.length >= 8) flushSeen();
          }, 1000));
        } else {
          window.clearTimeout(timers.get(card));
        }
      });
    },
    { threshold: 0.5 }
  );
  const watchCards = () => document.querySelectorAll('[data-card]').forEach((c) => cardObserver.observe(c));
  watchCards();
  new MutationObserver(watchCards).observe(document.body, { childList: true, subtree: true });
  window.setInterval(flushSeen, 4000);

  document.addEventListener('click', (event) => {
    const t = event.target;
    const card = t.closest('[data-card]');
    if (card) {
      const info = cardInfo(card);
      if (t.closest('[data-quick-add]')) send('quick_add_open', info);
      else if (t.closest('[data-card-thumb]')) send('card_photo_tap', info);
      else if (t.closest('a')) send('product_click', info);
    }
    if (t.closest('.search-row [data-quick-add]')) send('quick_add_open', { list: 'search' });
    if (t.closest('[data-size-guide-toggle]')) send('size_guide_open', pdp.base());
    if (t.closest('[data-whatsapp]')) send('whatsapp_click', { ...pdp.base(), size: pdp.size(), from: t.closest('[data-whatsapp-float]') ? 'float' : 'product' });
    if (t.closest('[data-main-atc], [data-sticky-atc]')) pdp.state.atcTaps += 1;
    if (t.closest('[data-buy-now]')) send('buy_now_click', pdp.base());
    if (t.closest('[data-qty-change]')) send('cart_quantity_change', { to: t.closest('[data-qty-change]').dataset.qtyChange });
    if (t.closest('.cart-drawer__checkout, [name="checkout"]')) send('checkout_click', { from: t.closest('dialog') ? 'drawer' : 'cart_page', ...cartState() });
    if (t.closest('.search-drawer__chip')) send('search_chip', { label: t.closest('.search-drawer__chip').textContent.trim() });
    if (t.closest('[data-filter-open]')) send('filters_open', {});
    if (t.closest('[data-load-more]')) send('load_more', { list: listName(t) });
    if (t.closest('.hero__btn, .collage__tile, .tiles__link, .video-block__link')) {
      const el = t.closest('a');
      send('home_block_click', { block: el.className.split(' ')[0], href: el.getAttribute('href') });
    }
  });

  /* ---------- Product page engagement ---------- */
  const productEl = document.querySelector('product-page:not(.qa)');
  const pdp = {
    state: { photos: new Set(), sizeGuide: false, accordions: new Set(), sizes: [], soldoutTaps: 0, atcTaps: 0, maxScroll: 0, start: Date.now(), added: false, whatsapp: false },
    base() {
      const json = productEl && productEl.querySelector('[data-product-json]');
      let handle = '';
      try {
        handle = json ? JSON.parse(json.textContent).handle : '';
      } catch (_) {
        handle = '';
      }
      return { product: handle || path.split('/').pop() };
    },
    size() {
      return [...document.querySelectorAll('.pp-form input[type="radio"]:checked')].map((i) => i.dataset.value).join(' / ');
    },
  };

  if (productEl) {
    const slides = [...productEl.querySelectorAll('.pp-media__slide')];
    const photoObserver = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && pdp.state.photos.add(slides.indexOf(e.target))),
      { threshold: 0.6 }
    );
    slides.forEach((s) => photoObserver.observe(s));

    document.addEventListener(
      'toggle',
      (event) => {
        const acc = event.target.closest && event.target.closest('.pp-acc');
        if (acc && acc.open) {
          const name = acc.querySelector('.pp-acc__title').textContent.trim();
          pdp.state.accordions.add(name);
          send('product_info_open', { ...pdp.base(), section: name });
        }
      },
      true
    );

    productEl.addEventListener('change', (event) => {
      const input = event.target.closest('input[type="radio"]');
      if (!input) return;
      const soldOut = input.hasAttribute('data-soldout');
      pdp.state.sizes.push(input.dataset.value);
      if (soldOut) pdp.state.soldoutTaps += 1;
      send(soldOut ? 'soldout_size_select' : 'size_select', { ...pdp.base(), size: input.dataset.value });
    });

    window.addEventListener(
      'scroll',
      () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0) pdp.state.maxScroll = Math.max(pdp.state.maxScroll, Math.round((window.scrollY / max) * 100));
      },
      { passive: true }
    );

    document.addEventListener('cart:item-added', () => {
      pdp.state.added = true;
    });

    // One summary per product view, when the visitor leaves or hides the tab:
    // the "likely culprit" is the last thing they did before leaving
    let summarised = false;
    const summarise = () => {
      if (summarised) return;
      summarised = true;
      const s = pdp.state;
      let lastStep = 'viewed';
      if (s.photos.size > 1) lastStep = 'browsed_photos';
      if (s.accordions.size) lastStep = `read_${[...s.accordions].pop().toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
      if (s.sizeGuide) lastStep = 'size_guide';
      if (s.sizes.length) lastStep = 'chose_size';
      if (s.soldoutTaps) lastStep = 'soldout_size';
      if (s.atcTaps && !s.added) lastStep = 'tapped_add_no_size';
      if (s.added) lastStep = 'added_to_cart';
      send('product_engagement', {
        ...pdp.base(),
        seconds: Math.round((Date.now() - s.start) / 1000),
        photos_seen: s.photos.size,
        photos_total: slides.length,
        scroll_pct: s.maxScroll,
        size_guide: s.sizeGuide,
        info_opened: [...s.accordions].join(','),
        sizes_tried: s.sizes.join(','),
        soldout_taps: s.soldoutTaps,
        added: s.added,
        last_step: lastStep,
      });
    };
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-size-guide-toggle]')) pdp.state.sizeGuide = true;
    });
    document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && summarise());
    window.addEventListener('pagehide', summarise);
  }

  /* ---------- Cart drawer ---------- */
  const cartState = () => {
    const content = document.querySelector('[data-cart-content]');
    const bar = document.querySelector('.cart-drawer__bar');
    return {
      items: content ? Number(content.dataset.count || 0) : 0,
      free_shipping_pct: bar ? Number(bar.getAttribute('aria-valuenow') || 0) : 0,
    };
  };
  const cartDialog = document.querySelector('[data-cart-dialog]');
  if (cartDialog) {
    let openedAt = 0;
    let checkingOut = false;
    new MutationObserver(() => {
      if (cartDialog.open) {
        openedAt = Date.now();
        send('cart_open', cartState());
      }
    }).observe(cartDialog, { attributes: true, attributeFilter: ['open'] });
    cartDialog.addEventListener('click', (e) => {
      if (e.target.closest('.cart-drawer__checkout, .cart-drawer__wallets')) checkingOut = true;
    });
    cartDialog.addEventListener('close', () => {
      if (!checkingOut) send('cart_close_without_checkout', { ...cartState(), seconds: Math.round((Date.now() - openedAt) / 1000) });
      checkingOut = false;
    });
  }

  /* ---------- Search ---------- */
  const results = document.querySelector('[data-search-results]');
  if (results) {
    new MutationObserver(() => {
      const box = results.querySelector('[data-predictive]');
      if (!box) return;
      const terms = box.dataset.terms || '';
      const count = Number(box.dataset.count || 0);
      if (terms) send(count ? 'search_suggest' : 'search_no_results', { terms: terms.slice(0, 60), count, where: 'drawer' });
    }).observe(results, { childList: true });
  }
  const srch = document.querySelector('[data-search-page]');
  if (srch && srch.dataset.terms) {
    const count = Number(srch.dataset.count || 0);
    send(count ? 'search_results' : 'search_no_results', { terms: srch.dataset.terms.slice(0, 60), count, where: 'page' });
  }

  /* ---------- Collection filters / sorting ---------- */
  if (/\/collections\//.test(path)) {
    const params = new URLSearchParams(window.location.search);
    const filters = [...params.keys()].filter((k) => k.startsWith('filter.'));
    if (filters.length || params.get('sort_by')) {
      send('filters_applied', { filters: filters.map((k) => `${k.replace('filter.', '')}=${params.getAll(k).join('|')}`).join(','), sort: params.get('sort_by') || '' });
    }
  }

  /* ---------- Page depth (all pages) ---------- */
  let depthSent = 0;
  window.addEventListener(
    'scroll',
    () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = (window.scrollY / max) * 100;
      const mark = [25, 50, 75, 100].find((m) => pct >= m && m > depthSent && m === Math.max(...[25, 50, 75, 100].filter((x) => pct >= x)));
      if (mark) {
        depthSent = mark;
        send('scroll_depth', { page_type: page, percent: mark });
      }
    },
    { passive: true }
  );
})();
